import React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader } from '../components/page-headers/page-headers';
import {
  Row,
  Col,
  Input,
  Form,
  Upload,
  message,
  Switch,
  Table,
  Select,
  Popconfirm,
  Button,
  Modal,
  Tag,
  DatePicker,
  Radio,
  Spin,
  Badge,
  Tooltip,
} from 'antd';
import axios from '../backendService'; // Import axios for API requests
import { GlobalUtilityStyle, PaginationStyle } from '../container/styled';
import { BiEditAlt } from 'react-icons/bi';
import { MdDelete } from 'react-icons/md';
import PlayVideoModal from '../components/video-modals/PlayVideoModal';

const VideoLibrary = () => {
  const [videoData, setVideoData] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [currVideo, setCurrVideo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoURL, setVideoURL] = useState(null);
  const [playVideo, setPlayVideo] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalVideos: 0,
    pageSize: 10,
  });
  // Memoize the API call function
   // Optimize the API call to avoid redundant state changes
   const fetchData = useCallback(
    async (page = 1, search = '') => {
      try {
        setLoading(true); // Only set loading once
        const response = await axios.get('/videos/get-all-videos', {
          params: { page, limit: pagination.pageSize, searchQuery: search },
        });
        const { videosWithUniqueURLs, pagination: paginationData } = response.data;
        setVideoData(videosWithUniqueURLs); // Update data
        setPagination(paginationData); // Update pagination
      } catch (error) {
        console.error('Error fetching videos:', error);
        message.error('Failed to fetch videos');
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize],
  );

  // Initial Fetch
  useEffect(() => {
    fetchData(); // Only call once during initial render
  }, [fetchData]);

  // Debounce for Search
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFetch = useMemo(() => debounce(fetchData, 500), [fetchData]);  
  
  const onSearchQueryChange = (e) => {
    const search = e.target.value;
    setSearchQuery(search);
    debouncedFetch(search);
  };
  
  // useEffect(() => {
  //   const initialFetch = fetchData();
  //   return () => initialFetch;
  // }, [fetchData]);

  const columns = useMemo(() =>  [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title) => (
        <Tooltip title={title}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '150px',
            }}
          >
            {title}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Teacher',
      dataIndex: 'teacher',
      key: 'teacher',
      align: 'center',
      render: (teacher) => <span className="flex items-center justify-center">{teacher.userName}</span>,
    },
    {
      title: 'Publish Date',
      dataIndex: 'publishDate',
      key: 'publishDate',
      align: 'center',
      render: (publishDate) => <span className="flex items-center justify-center">{publishDate}</span>,
    },
    {
      title: 'Start Date',
      dataIndex: 'startTime',
      key: 'startTime',
      align: 'center',
      render: (startTime) => <span className="flex items-center justify-center">{startTime}</span>,
    },
    {
      title: 'End Date',
      dataIndex: 'endTime',
      key: 'endTime',
      align: 'center',
      render: (endTime) => <span className="flex items-center justify-center">{endTime}</span>,
    },
    {
      title: 'Course',
      dataIndex: 'course',
      key: 'course',
      align: 'center',
      render: (course) => <span className="flex items-center justify-center">{course.courseName}</span>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      align: 'center',
      render: (subject) => <span className="flex items-center justify-center">{subject.subjectName}</span>,
    },
    {
      title: 'Chapter',
      dataIndex: 'chapter',
      key: 'chapter',
      align: 'center',
      render: (chapter) => <span className="flex items-center justify-center">{chapter.chapterName}</span>,
    },
    {
      title: 'Topic',
      dataIndex: 'topic',
      key: 'topic',
      align: 'center',
      render: (topic) => <span className="flex items-center justify-center">{topic.topicName}</span>,
    },
    {
      title: 'Watch Video',
      dataIndex: 'watchVideo',
      key: 'watchVideo',
      align: 'center',
      render: (text, record) => (
        <div className="flex items-center justify-center gap-3">
          <Button onClick={() => handleVideoModal(record.videoURL)} size="small" type="primary" className="px-5 h-9">
            Watch Video
          </Button>
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title={`Delete Lecture`}>
            <MdDelete
              color={'red'}
              className="mx-2 w-4 h-4 cursor-pointer"
              onClick={() => handleVideoDelete(record._id)}
            />
          </Tooltip>
        </div>
      ),
    },
 ],[] );
  const handleVideoDelete = (id) => {
    setCurrVideo(id);
    setOpenDelete(true);
  };

  const closeDeleteModal = () => {
    setCurrVideo(null);
    setOpenDelete(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`/videos/delete-video-file/${currVideo}/`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.data.message === 'All lectures with this video file are now deleted.') {
        message.success(response.data.message);
        fetchData();
        closeDeleteModal();
        setLoading(false);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Filter the data based on the search query
  const filteredData = videoData.filter((item) => item.title.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleVideoModal = (url) => {
    setVideoURL(url);
    setPlayVideo(true);
  };

  const closeVideoModal = () => {
    setVideoURL(null);
    setPlayVideo(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    fetchData(pagination.current, searchQuery);
  };
  return (
    <>
      <PageHeader
        className="flex items-center justify-between px-8 xl:px-[15px] pt-7 pb-6 sm:pb-[30px] bg-transparent sm:flex-col"
        title="  "
      />
      <main className="min-h-[715px] lg:min-h-[580px] px-8 xl:px-[15px] bg-transparent">
        <Row gutter={30}>
          <Col xs={24}>
            <>
              <div className="bg-white dark:bg-white10 m-0 text-theme-gray dark:text-white60 text-[15px] rounded-10 relative p-[25px]">
                <div className="text-dark dark:text-white87 font-medium text-[17px] flex items-center justify-between gap-[15px] sm:justify-left mb-5">
                  <div className="flex items-center justify-between gap-[5px]">
                    <h1 className="mb-0 inline-block text-ellipsis text-[18px] font-semibold sm:text-center w-full">
                      Video Library
                    </h1>
                    <Input
                      className="w-[150px]"
                      value={searchQuery}
                      onChange={onSearchQueryChange}
                      placeholder="Search by title..."
                    />
                  </div>
                </div>

                <div className="mt-0.5">
                  <GlobalUtilityStyle>
                    <PaginationStyle>
                      <div className="ant-pagination-custom-style table-responsive hover-tr-none table-th-shape-none table-last-th-text-right table-th-border-none table-head-rounded table-selection-col-pl-25 table-tr-selected-background-transparent table-td-border-none bg-white dark:bg-transparent rounded-[10px] ltr:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:first-child]:rounded-l-10 rtl:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:first-child]:rounded-r-10 rtl:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:first-child]:rounded-none ltr:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:last-child]:rounded-r-10 rtl:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:last-child]:rounded-l-10 rtl:[&>div>div>div>div>div>.ant-table-content>table>thead>tr>th:last-child]:rounded-none">
                        <Table
                          dataSource={filteredData}
                          columns={columns}
                          rowKey={(record) => record._id}
                          // pagination={{
                          //   defaultPageSize: 7,
                          //   total: videoData.length,
                          //   showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                          //   className:
                          //     '[&>.ant-pagination-total-text]:border-none ssm:[&>.ant-pagination-total-text]:w-full [&>.ant-pagination-total-text]:text-center [&>.ant-pagination-total-text]:text-dark dark:[&>.ant-pagination-total-text]:text-white87 [&>.ant-pagination-total-text]:font-medium [&>.ant-pagination-total-text]:text-[17px md:justify-center',
                          // }}
                          pagination={{
                            current: pagination.currentPage,
                            pageSize: pagination.pageSize,
                            total: pagination.totalVideos,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            showSizeChanger: false,
                          }}
                          loading={loading}
                          onChange={handleTableChange}
                        />
                      </div>
                    </PaginationStyle>
                  </GlobalUtilityStyle>
                </div>
              </div>
            </>
          </Col>
        </Row>
      </main>
      {/* Watch video modal */}
      <PlayVideoModal videoURL={videoURL} playVideo={playVideo} closeVideoModal={closeVideoModal} />
      {/* Delete modal */}
      <Modal
        title={
          <div>
            <h1>Are you sure you want to delete this video file?</h1>
            <span className="text-sm font-normal">
              This will delete original video file and will delete all lectures using this video file. Please make sure
              no lectures are scheduled to stream using this video file.
            </span>
          </div>
        }
        open={openDelete}
        onCancel={closeDeleteModal}
        footer={null}
      >
        <div className="flex items-center justify-end gap-2">
          <Button onClick={closeDeleteModal} type={'ghost'}>
            Cancel
          </Button>
          <Button disable={loading} onClick={handleDelete} type={'danger'}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default VideoLibrary;
